/**
 * Review step: exactly what the import will do, before it does anything.
 */

import {
  AlertTriangle,
  Building2,
  Info,
  Package,
  Plane,
  Trash2,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { UnmatchedItemsList } from './UnmatchedItemsList'
import type { ImportPlan, PlannedOp } from '@/lib/hangarmax-import'

interface ImportPreviewProps {
  plan: ImportPlan
}

const OP_LABEL: Record<PlannedOp, string> = {
  create: 'New',
  update: 'Update',
  noop: 'Unchanged',
  unmatched: 'Not imported',
  source_deleted: 'Removed in source',
}

function OpBadge({ op }: { op: PlannedOp }) {
  const variant =
    op === 'create' ? 'default' : op === 'update' ? 'secondary' : 'outline'
  return (
    <Badge variant={variant} className="shrink-0 text-[10px]">
      {OP_LABEL[op]}
    </Badge>
  )
}

function Section({
  icon: Icon,
  title,
  count,
  children,
}: {
  icon: typeof Building2
  title: string
  count: number
  children: React.ReactNode
}) {
  if (count === 0) return null
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Icon className="h-4 w-4 text-muted-foreground" />
        {title}
        <span className="text-muted-foreground">({count})</span>
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  )
}

function Row({
  op,
  label,
  detail,
  notes,
}: {
  op: PlannedOp
  label: string
  detail?: string | null
  notes?: string[]
}) {
  return (
    <div className="rounded-md border px-3 py-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-sm">{label}</div>
          {detail && (
            <div className="truncate text-xs text-muted-foreground">{detail}</div>
          )}
        </div>
        <OpBadge op={op} />
      </div>
      {notes && notes.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {notes.map((note) => (
            <li
              key={note}
              className="flex items-start gap-1 text-[11px] text-muted-foreground"
            >
              <Info className="mt-0.5 h-3 w-3 shrink-0" />
              <span>{note}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export function ImportPreview({ plan }: ImportPreviewProps) {
  const { counts } = plan

  const matchedAircraft = plan.aircraft.filter((a) => a.match.aircraftTypeId).length
  const proceduralAircraft = plan.aircraft.length - matchedAircraft

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        <Stat label="New" value={counts.created} />
        <Stat label="Updated" value={counts.updated} />
        <Stat label="Unchanged" value={counts.unchanged} />
        <Stat label="Not imported" value={counts.unmatched} />
        <Stat label="Removed in source" value={counts.sourceDeleted} />
      </div>

      {plan.sceneUnchanged && (
        <div className="flex items-start gap-2 rounded-md border bg-muted/40 px-3 py-2 text-xs">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            The source layout has not changed since the last sync. Re-running will
            only retry items that could not be imported before.
          </span>
        </div>
      )}

      {plan.warnings.length > 0 && (
        <div className="space-y-1">
          {plan.warnings.map((warning) => (
            <div
              key={warning}
              className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200"
            >
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{warning}</span>
            </div>
          ))}
        </div>
      )}

      <ScrollArea className="max-h-[45vh] pr-3">
        <div className="space-y-5">
          <Section icon={Building2} title="Buildings" count={plan.buildings.length}>
            {plan.buildings.map((building) => (
              <Row
                key={building.sourceObjectId}
                op={building.op}
                label={building.label}
                detail={`${Math.round(building.area.widthFt)} × ${Math.round(
                  building.area.depthFt
                )} ft · ${Math.round(building.area.rotationDeg)}°${
                  building.approximate ? ' · approximated' : ''
                }`}
                notes={building.fidelityNotes}
              />
            ))}
          </Section>

          <Section icon={Plane} title="Aircraft" count={plan.aircraft.length}>
            {plan.aircraft.length > 0 && (
              <p className="pb-1 text-xs text-muted-foreground">
                {matchedAircraft} matched to a PlaneMAX aircraft type
                {proceduralAircraft > 0 &&
                  `, ${proceduralAircraft} imported with HangarMax dimensions (rendered procedurally)`}
                . All arrive with temporary tail numbers.
              </p>
            )}
            {plan.aircraft.map((aircraft) => (
              <Row
                key={aircraft.sourceObjectId}
                op={aircraft.op}
                label={aircraft.label}
                detail={
                  aircraft.match.typeLabel
                    ? `${aircraft.match.typeLabel} · matched by ${aircraft.match.method}`
                    : 'No aircraft type match'
                }
                notes={aircraft.fidelityNotes}
              />
            ))}
          </Section>

          <Section icon={Package} title="Obstacles" count={plan.obstacles.length}>
            {plan.obstacles.map((obstacle) => (
              <Row
                key={obstacle.sourceObjectId}
                op={obstacle.op}
                label={obstacle.name}
                detail={`${obstacle.obstacleType} · ${obstacle.shape}`}
              />
            ))}
          </Section>

          <Section
            icon={Trash2}
            title="Removed from the HangarMax layout"
            count={plan.sourceDeleted.length}
          >
            <p className="pb-1 text-xs text-muted-foreground">
              These are flagged only. PlaneMAX rows are never deleted by an import.
            </p>
            {plan.sourceDeleted.map((entry) => (
              <Row key={entry.linkId} op="source_deleted" label={entry.label} />
            ))}
          </Section>

          <UnmatchedItemsList
            items={plan.skipped.map((s) => ({ label: s.label, reason: s.reason }))}
          />
        </div>
      </ScrollArea>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border px-3 py-2">
      <div className="text-lg font-semibold tabular-nums">{value}</div>
      <div className="text-[11px] text-muted-foreground">{label}</div>
    </div>
  )
}
